'use strict'

const MUTATIONS = { POST: true, PUT: true, PATCH: true, DELETE: true }

function pathId (value) {

  try {

    return decodeURIComponent(value)
  } catch (error) {

    return value
  }
}

function settingsChange (body) {

  const source = body && typeof body === 'object' ? body : {}
  const keys = []
  const labels = []
  const sections = [
    [['theme', 'customCss'], 'settings:appearance', 'appearance'],
    [['timeZone'], 'settings:dates', 'the time zone'],
    [['userSession', 'authenticatorPolicy', 'passwordPolicy'], 'settings:users', 'user settings'],
    [['oauthProviders'], 'settings:oauth', 'sign-in providers'],
    [['oauthServer'], 'settings:oauth-server', 'the OAuth server'],
    [['mail'], 'settings:mail', 'mail settings'],
    [['menuConfig', 'logoBase64', 'appDisplayName'], 'settings:menu', 'the menu'],
    [['redirectHostAllowList'], 'settings:embed', 'embed settings'],
    [['customCode'], 'settings:code', 'custom code']
  ]

  sections.forEach(([fields, key, label]) => {

    if (fields.some((field) => Object.prototype.hasOwnProperty.call(source, field))) {

      keys.push(key)
      labels.push(label)
    }
  })

  if (!keys.length) return { keys: ['settings'], label: 'app settings' }

  return { keys, label: labels.length === 1 ? labels[0] : 'app settings' }
}

function designerChangeFromRequest ({ method, path, body }) {

  if (!MUTATIONS[method]) return null

  const route = path || ''

  if (method === 'POST' && /^\/automations\/[^/]+\/execute$/.test(route)) return null
  if (method === 'POST' && /^\/step-workflows\/[^/]+\/execute$/.test(route)) return null
  if (method === 'POST' && route === '/menu-values/preview') return null
  if (method === 'POST' && /^\/data\/[^/]+\/import\/preview$/.test(route)) return null
  if (route.startsWith('/ai-query') || route.startsWith('/ai-usage') || route.startsWith('/ai-build/history')) return null
  if (route.startsWith('/analytics') || route.startsWith('/jobs') || route.startsWith('/record-history') || route.startsWith('/stats') || route.startsWith('/schedule-logs')) return null

  if (
    route.startsWith('/ai-build')
    || route.startsWith('/ai-pages')
    || route.startsWith('/ai-spreadsheets')
    || route.startsWith('/ai-css')
    || route.startsWith('/ai-views')
  ) {

    return { keys: ['application'], label: 'this application' }
  }

  if (route === '/pages' || route === '/pages/' || route === '/pages/arrange' || route === '/pages/reorder') {

    return { keys: ['pages'], label: 'pages' }
  }

  let match = route.match(/^\/pages\/([^/]+)\/copy$/)

  if (match) return { keys: ['pages', `page:${pathId(match[1])}`], label: 'pages' }

  if (/^\/pages\/[^/]+\/views\/[^/]+\/transfer$/.test(route)) {

    return { keys: ['pages'], label: 'pages' }
  }

  match = route.match(/^\/pages\/([^/]+)$/)

  if (match) {

    const id = pathId(match[1])

    return { keys: ['pages', `page:${id}`], label: 'this page' }
  }

  if (route === '/spreadsheets' || route === '/spreadsheets/' || route === '/spreadsheets/reorder') {

    return { keys: ['spreadsheets'], label: 'spreadsheets' }
  }

  if (/^\/spreadsheets\/[^/]+\/copy$/.test(route)) {

    return { keys: ['spreadsheets'], label: 'spreadsheets' }
  }

  match = route.match(/^\/spreadsheets\/([^/]+)\/columns(?:\/.*)?$/)

  if (match) {

    const id = pathId(match[1])

    return { keys: ['spreadsheets', `spreadsheet:${id}`], label: 'this spreadsheet' }
  }

  match = route.match(/^\/spreadsheets\/([^/]+)\/workflow-hooks$/)

  if (match) return { keys: [`spreadsheet:${pathId(match[1])}`], label: 'workflow hooks' }

  match = route.match(/^\/spreadsheets\/([^/]+)$/)

  if (match) {

    const id = pathId(match[1])

    return { keys: ['spreadsheets', `spreadsheet:${id}`], label: 'this spreadsheet' }
  }

  match = route.match(/^\/data\/([^/]+)(?:\/.*)?$/)

  if (match) return { keys: [`records:${pathId(match[1])}`], label: 'records in this spreadsheet' }

  if (route === '/automations' || route === '/automations/' || route === '/automations/reorder') {

    return { keys: ['automations'], label: 'automations' }
  }

  match = route.match(/^\/automations\/([^/]+)$/)

  if (match) {

    const id = pathId(match[1])

    return { keys: ['automations', `automation:${id}`], label: 'this automation' }
  }

  if (route === '/step-workflows' || route === '/step-workflows/') {

    return { keys: ['workflows'], label: 'workflows' }
  }

  match = route.match(/^\/step-workflows\/([^/]+)$/)

  if (match) {

    const id = pathId(match[1])

    return { keys: ['workflows', `workflow:${id}`], label: 'this workflow' }
  }

  if (route === '/schedules' || route === '/schedules/') {

    return { keys: ['schedules'], label: 'scheduled tasks' }
  }

  match = route.match(/^\/schedules\/([^/]+)$/)

  if (match) return { keys: ['schedules', `schedule:${pathId(match[1])}`], label: 'this scheduled task' }

  if (route === '/settings') return settingsChange(body)

  if (route === '/api-keys' || route.startsWith('/api-keys/')) {

    return { keys: ['api-keys'], label: 'API keys' }
  }

  if (route === '/custom-themes' || route.startsWith('/custom-themes/')) {

    return { keys: ['settings:appearance', 'custom-themes'], label: 'themes' }
  }

  return null
}

function normalizeDesignerTabId (value) {

  const raw = Array.isArray(value) ? value[0] : value

  if (typeof raw !== 'string') return ''

  const trimmed = raw.trim()

  if (!/^[A-Za-z0-9-]{8,80}$/.test(trimmed)) return ''

  return trimmed
}

function actorFromSave ({ email, tabId, apiKey }) {

  const normalizedTab = normalizeDesignerTabId(tabId)

  if (email) {

    return { userKey: String(email), userLabel: String(email), tabId: normalizedTab }
  }

  if (apiKey) {

    const hint = String(apiKey).slice(0, 12)

    return { userKey: `api:${hint}`, userLabel: 'An API client', tabId: '' }
  }

  return { userKey: '', userLabel: 'Another designer', tabId: normalizedTab }
}

function actorKind ({ localTabId, remoteTabId, localUserKey, remoteUserKey }) {

  if (remoteTabId && localTabId && remoteTabId === localTabId) return 'self'

  if (remoteUserKey && localUserKey && remoteUserKey === localUserKey) return 'tab'

  return 'user'
}

function changeHits (change, keys) {

  const incoming = change && Array.isArray(change.keys) ? change.keys : []

  if (!keys || !keys.length) return false

  if (incoming.includes('application')) return true

  return keys.some((key) => key && incoming.includes(key))
}

function conflictWarning ({ kind, actorLabel, resourceLabel }) {

  const resource = resourceLabel || 'this'

  if (kind === 'tab') {

    return `Another tab saved changes to ${resource}. Saving here will undo that work.`
  }

  const who = actorLabel && actorLabel !== 'Another designer' ? actorLabel : 'Another designer'

  return `${who} saved changes to ${resource}. Saving here will undo their work.`
}

module.exports = {
  designerChangeFromRequest,
  normalizeDesignerTabId,
  actorFromSave,
  actorKind,
  changeHits,
  conflictWarning
}
